// ============ RAVE BODYGRAPH ============
// The real chart, not a schematic: all 64 gates in their canonical positions,
// all 36 channels, the 9 centre shapes. Geometry ported from the hd-core engine
// in D:\Humen Design (viewBox 460×800, the classic portrait layout).
//
// What the drawing says, in the language HD readers already know:
//   • a centre is FILLED when a channel through it is complete — that is where
//     you are consistent. An open centre is where you take in other people.
//   • a channel is drawn in halves, each half coloured by which side switched it
//     on: RED = Design (the body, unconscious), WHITE = Personality (what you
//     know about yourself). Both sides on the same gate = violet.
//   • a gate with only one end lit is drawn as a stub — the half you carry and
//     keep meeting in other people.

import React from 'react';
import { CHANNELS, GATE_CENTER } from '../lib/humandesign';

export const DESIGN_C = '#ff5c4d';       // design activation (red, per HD convention)
export const PERSONALITY_C = '#e9e9f2';  // personality activation (white)
export const BOTH_C = '#a78bfa';         // the same gate lit from both sides

/** Canonical [x, y] of every gate marker. */
const GATE_POS: Record<number, [number, number]> = {
  64: [206, 80], 61: [230, 77], 63: [254, 80],
  47: [206, 120], 24: [230, 117], 4: [254, 120], 17: [212, 148], 11: [248, 148], 43: [230, 172],
  62: [207, 224], 23: [230, 221], 56: [253, 224], 16: [199, 247], 35: [261, 247],
  12: [261, 269], 20: [199, 290], 45: [261, 290], 31: [212, 292], 8: [230, 292], 33: [248, 292],
  1: [223, 347], 13: [241, 349], 7: [211, 357], 10: [197, 378], 25: [263, 378],
  15: [214, 405], 46: [246, 405], 2: [230, 415],
  21: [322, 361], 51: [312, 388], 26: [305, 405], 40: [325, 400],
  5: [208, 484], 14: [230, 481], 29: [252, 484], 34: [199, 506],
  27: [199, 542], 59: [261, 531], 42: [212, 547], 3: [230, 548], 9: [250, 547],
  48: [74, 478], 57: [98, 499], 44: [110, 519], 50: [114, 539], 32: [98, 557], 28: [80, 567], 18: [72, 583],
  36: [390, 476], 22: [372, 498], 37: [354, 506], 6: [350, 529], 49: [366, 549], 55: [384, 557], 30: [390, 577],
  53: [208, 613], 60: [230, 611], 52: [252, 613], 54: [199, 636], 19: [261, 636],
  38: [210, 660], 39: [250, 660], 58: [212, 683], 41: [248, 683],
};

type Shape = { kind: 'poly'; points: string } | { kind: 'rect'; x: number; y: number; w: number; h: number };
const CENTER_SHAPE: Record<string, Shape> = {
  head: { kind: 'poly', points: '230,22 180,96 280,96' },
  ajna: { kind: 'poly', points: '182,102 278,102 230,184' },
  throat: { kind: 'rect', x: 190, y: 210, w: 80, h: 94 },
  g: { kind: 'poly', points: '230,330 289,378 230,426 171,378' },
  heart: { kind: 'poly', points: '336,356 336,420 296,388' },
  sacral: { kind: 'rect', x: 190, y: 468, w: 80, h: 92 },
  splenic: { kind: 'poly', points: '62,468 62,584 152,526' },
  solar: { kind: 'poly', points: '398,468 398,584 308,526' },
  root: { kind: 'rect', x: 190, y: 600, w: 80, h: 94 },
};

/** Centre fill when defined — the classic palette, so the chart reads as a chart. */
const CENTER_COLOR: Record<string, string> = {
  head: '#f0c75a', ajna: '#8bc34a', throat: '#a3866f', g: '#f0c75a', heart: '#ff3b5c',
  sacral: '#ff3b5c', splenic: '#a3866f', solar: '#a3866f', root: '#8d6e63',
};

const CENTER_LABEL: Record<string, [string, string]> = {
  head: ['ศีรษะ', 'Head'], ajna: ['อาชญะ', 'Ajna'], throat: ['ลำคอ', 'Throat'],
  g: ['ตัวตน (G)', 'G'], heart: ['หัวใจ', 'Heart'], sacral: ['ศักดิ์สิทธิ์', 'Sacral'],
  splenic: ['ม้าม', 'Spleen'], solar: ['อารมณ์', 'Solar'], root: ['ราก', 'Root'],
};

export interface BodygraphProps {
  definedCenters: string[];
  /** Gates lit by the Design side (88 days before birth). */
  designGates: number[];
  /** Gates lit by the Personality side (the moment of birth). */
  personalityGates: number[];
  onGate?: (gate: number) => void;
  showLabels?: boolean;
  en?: boolean;
}

export function RaveBodygraph({ definedCenters, designGates, personalityGates, onGate, showLabels = true, en = false }: BodygraphProps) {
  const defined = new Set(definedCenters);
  const dSet = new Set(designGates);
  const pSet = new Set(personalityGates);
  const active = (g: number) => dSet.has(g) || pSet.has(g);
  const gateColor = (g: number) =>
    dSet.has(g) && pSet.has(g) ? BOTH_C : dSet.has(g) ? DESIGN_C : pSet.has(g) ? PERSONALITY_C : null;

  return (
    <svg viewBox="0 0 460 800" className="mm-rave" role="img"
      aria-label={en ? 'Human Design bodygraph' : 'แผนภาพ Bodygraph'}>
      {/* Skeleton first: every channel that exists, so the shape of the system
          is visible and a reader can see what they do NOT have. */}
      <g className="mm-rave-skeleton">
        {CHANNELS.map(([a, b]) => {
          const pa = GATE_POS[a], pb = GATE_POS[b];
          if (!pa || !pb) return null;
          return <line key={`s${a}-${b}`} x1={pa[0]} y1={pa[1]} x2={pb[0]} y2={pb[1]} />;
        })}
      </g>

      {/* Then the halves that are actually switched on, each in its own colour. */}
      <g className="mm-rave-live">
        {CHANNELS.map(([a, b]) => {
          const pa = GATE_POS[a], pb = GATE_POS[b];
          if (!pa || !pb) return null;
          const mid: [number, number] = [(pa[0] + pb[0]) / 2, (pa[1] + pb[1]) / 2];
          const halves = [[a, pa], [b, pb]] as [number, [number, number]][];
          return halves.map(([g, p]) => {
            const c = gateColor(g);
            if (!c) return null;
            const whole = active(a) && active(b);
            return (
              <line key={`h${a}-${b}-${g}`} x1={p[0]} y1={p[1]} x2={mid[0]} y2={mid[1]}
                stroke={c} strokeWidth={whole ? 4 : 2.4} strokeLinecap="round"
                opacity={whole ? 1 : 0.55} />
            );
          });
        })}
      </g>

      {/* Centres. A filled centre is a consistent one; an open centre is where
          the world gets in — which is the half of the chart people act from. */}
      {Object.entries(CENTER_SHAPE).map(([id, sh]) => {
        const on = defined.has(id);
        const fill = on ? CENTER_COLOR[id] : 'rgba(255,255,255,0.04)';
        const stroke = on ? CENTER_COLOR[id] : 'rgba(255,255,255,0.22)';
        return sh.kind === 'poly'
          ? <polygon key={id} points={sh.points} fill={fill} stroke={stroke} strokeWidth={1.6}
              className={`mm-rave-center ${on ? 'on' : ''}`} />
          : <rect key={id} x={sh.x} y={sh.y} width={sh.w} height={sh.h} rx={8} fill={fill} stroke={stroke}
              strokeWidth={1.6} className={`mm-rave-center ${on ? 'on' : ''}`} />;
      })}

      {/* Gate markers last so they sit above everything. */}
      <g className="mm-rave-gates">
        {Object.entries(GATE_POS).map(([gs, [x, y]]) => {
          const g = Number(gs);
          const c = gateColor(g);
          return (
            <g key={g} className={`mm-rave-gate ${c ? 'on' : ''}`} onClick={() => onGate?.(g)}>
              <circle cx={x} cy={y} r={9} fill={c || 'rgba(12,12,20,0.85)'}
                stroke={c || 'rgba(255,255,255,0.28)'} strokeWidth={1.2} />
              <text x={x} y={y + 3.2} textAnchor="middle"
                fill={c ? (c === PERSONALITY_C ? '#0b0b12' : '#0b0b12') : 'rgba(255,255,255,0.55)'}>{g}</text>
            </g>
          );
        })}
      </g>

      {showLabels && (
        <g className="mm-rave-labels">
          {Object.entries(CENTER_LABEL).map(([id, [th, enL]]) => {
            const sh = CENTER_SHAPE[id];
            const pos: Record<string, [number, number, string]> = {
              head: [230, 12, 'middle'], ajna: [292, 118, 'start'], throat: [166, 200, 'end'],
              g: [300, 340, 'start'], heart: [352, 348, 'start'], sacral: [166, 460, 'end'],
              splenic: [56, 460, 'end'], solar: [404, 460, 'start'], root: [166, 596, 'end'],
            };
            const [lx, ly, anchor] = pos[id] || [sh.kind === 'rect' ? sh.x : 230, 0, 'middle'];
            return <text key={id} x={lx} y={ly} textAnchor={anchor as any}
              className={defined.has(id) ? 'on' : ''}>{en ? enL : th}</text>;
          })}
        </g>
      )}
    </svg>
  );
}

/** Which centre a gate belongs to — used by the page for the open-centre read. */
export const gateCenter = (g: number) => GATE_CENTER[g];
